import { memo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRegisterForm } from '../hooks/useRegisterForm';
import { usePasswordStrength } from '../hooks/usePasswordStrength';

// ── Country data (ISO 3166-1 alpha-2) ────────────────────────────────────────

function countryFlag(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

const COUNTRIES: [string, string][] = [
  ['AF','Afghanistan'],['AL','Albania'],['DZ','Algeria'],['AR','Argentina'],
  ['AU','Australia'],['AT','Austria'],['BE','Belgium'],['BR','Brazil'],
  ['CA','Canada'],['CL','Chile'],['CN','China'],['CO','Colombia'],
  ['HR','Croatia'],['CZ','Czech Republic'],['DK','Denmark'],['EG','Egypt'],
  ['FI','Finland'],['FR','France'],['DE','Germany'],['GH','Ghana'],
  ['GR','Greece'],['HU','Hungary'],['IN','India'],['ID','Indonesia'],
  ['IE','Ireland'],['IL','Israel'],['IT','Italy'],['JP','Japan'],
  ['KE','Kenya'],['KR','South Korea'],['MX','Mexico'],['NL','Netherlands'],
  ['NZ','New Zealand'],['NG','Nigeria'],['NO','Norway'],['PK','Pakistan'],
  ['PH','Philippines'],['PL','Poland'],['PT','Portugal'],['RO','Romania'],
  ['RU','Russia'],['SA','Saudi Arabia'],['ZA','South Africa'],['ES','Spain'],
  ['SE','Sweden'],['CH','Switzerland'],['TW','Taiwan'],['TH','Thailand'],
  ['TR','Turkey'],['UA','Ukraine'],['GB','United Kingdom'],['US','United States'],
  ['VN','Vietnam'],
];

// ── Password Strength Meter ───────────────────────────────────────────────────

interface StrengthMeterProps {
  password: string;
  visible: boolean;
}

const StrengthMeter = memo(function StrengthMeter({ password, visible }: StrengthMeterProps) {
  const strength = usePasswordStrength(password);

  if (!visible) return null;

  return (
    <div className="strength-meter">
      <div className="strength-bars">
        {([1, 2, 3, 4] as const).map((level) => (
          <div
            key={level}
            className="strength-bar"
            style={{
              background: strength.score >= level ? strength.color : 'var(--border)',
            }}
          />
        ))}
      </div>
      {strength.score > 0 && (
        <span className="strength-label" style={{ color: strength.color }}>
          {strength.label}
        </span>
      )}
      <ul className="strength-rules">
        {strength.rules.map((rule) => (
          <li
            key={rule.key}
            className={`strength-rule ${rule.pass ? 'strength-rule--pass' : 'strength-rule--fail'}`}
          >
            <span className="strength-rule-icon">{rule.pass ? '✓' : '○'}</span>
            {rule.label}
          </li>
        ))}
      </ul>
    </div>
  );
});

// ── Field ─────────────────────────────────────────────────────────────────────

interface FieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  autoComplete?: string;
  value: string;
  error?: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  hint?: string;
}

const Field = memo(function Field({
  id, name, label, type = 'text', autoComplete, value, error, onChange, onBlur, disabled, hint,
}: FieldProps) {
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        className={`form-input${error ? ' form-input--error' : ''}`}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        aria-invalid={!!error}
      />
      {hint && !error && (
        <span id={`${id}-hint`} className="form-hint">{hint}</span>
      )}
      {error && (
        <span id={`${id}-error`} className="form-error" role="alert">{error}</span>
      )}
    </div>
  );
});

// ── Page ──────────────────────────────────────────────────────────────────────

// ── TO RE-ENABLE SIGNUPS: change false → true ────────────────────────────────
const SIGNUPS_OPEN = false;

export default function RegisterPage() {
  const { isAuthed } = useAuth();
  const {
    fields, errors, serverError, loading,
    handleChange, handleBlur, handleSubmit,
  } = useRegisterForm();

  if (isAuthed) return <Navigate to="/practice" replace />;

  if (!SIGNUPS_OPEN) {
    return (
      <div className="auth-page">
        <div className="auth-header">
          <h1 className="auth-title">signups closed</h1>
          <p className="auth-subtitle">We're not accepting new accounts right now. Check back soon.</p>
        </div>
        <p className="auth-footer"><Link to="/login">back to sign in</Link></p>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">create account</h1>
        <p className="auth-subtitle">
          Free forever. No credit card.
        </p>
      </div>

      <form
        className="auth-form"
        onSubmit={handleSubmit}
        noValidate
        aria-label="Create account"
      >
        <Field
          id="email"
          name="email"
          label="email"
          type="email"
          autoComplete="email"
          value={fields.email}
          error={errors.email}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading}
        />

        <Field
          id="username"
          name="username"
          label="username"
          autoComplete="username"
          value={fields.username}
          error={errors.username}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading}
          hint="3-30 characters. Letters, numbers, _ and - only."
        />

        <div className="form-field">
          <label className="form-label" htmlFor="password">password</label>
          <input
            id="password"
            name="password"
            className={`form-input${errors.password ? ' form-input--error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={fields.password}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={loading}
            aria-describedby={errors.password ? 'password-error' : 'password-strength'}
            aria-invalid={!!errors.password}
          />
          {errors.password && (
            <span id="password-error" className="form-error" role="alert">
              {errors.password}
            </span>
          )}
          <StrengthMeter
            password={fields.password}
            visible={fields.password.length > 0}
          />
        </div>

        <Field
          id="confirmPassword"
          name="confirmPassword"
          label="confirm password"
          type="password"
          autoComplete="new-password"
          value={fields.confirmPassword}
          error={errors.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={loading}
        />

        <div className="form-field">
          <label className="form-label" htmlFor="countryCode">country <span className="form-hint-inline">(optional)</span></label>
          <select
            id="countryCode"
            name="countryCode"
            className="form-input"
            value={fields.countryCode}
            onChange={(e) => handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>)}
            disabled={loading}
          >
            <option value="">— select country —</option>
            {COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>{countryFlag(code)} {name}</option>
            ))}
          </select>
        </div>

        {serverError && (
          <div className="form-server-error" role="alert">
            {serverError}
          </div>
        )}

        <button
          className="btn btn--primary auth-submit-btn"
          type="submit"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? (
            <span className="auth-btn-loading">
              <span className="auth-spinner" />
              creating account…
            </span>
          ) : (
            'create account'
          )}
        </button>
      </form>

      <p className="auth-footer">
        already have an account? <Link to="/login">sign in</Link>
      </p>
    </div>
  );
}
